import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import DealLink from "../DealLink";
import { daysInStage } from "../../lib/dealAge";
import { mapsHref, isAddressLinkable } from "../../lib/mapsHref";
import { fmtSupabaseError } from "../../lib/fmtError";

/**
 * Executive Drone Overview — stats dashboard for Greg, Chad, TLs.
 *
 * Shows:
 *   - KPI cards (total pipeline, SLA compliance, conversion rate)
 *   - Stage breakdown with counts
 *   - SLA issues (overdue deals)
 *   - Recent activity
 */

const DRONE_STAGES = [
  "pending_asm_review",
  "appointment_received",
  "drone_scheduled",
  "drone_complete",
  "report_in_progress",
  "qc_review",
  "report_approved",
  "needs_climb_inspection",
  "insufficient_damage",
] as const;

const STAGE_LABELS: Record<string, string> = {
  pending_asm_review: "Pending ASM",
  appointment_received: "Appt Received",
  drone_scheduled: "Scheduled",
  drone_complete: "Drone Complete",
  report_in_progress: "Report Processing",
  qc_review: "QC Review",
  report_approved: "Approved",
  needs_climb_inspection: "Needs Climb",
  insufficient_damage: "Insufficient",
};

const STAGE_COLORS: Record<string, string> = {
  pending_asm_review: "bg-slate-200 text-slate-700",
  appointment_received: "bg-blue-100 text-blue-700",
  drone_scheduled: "bg-indigo-100 text-indigo-700",
  drone_complete: "bg-emerald-100 text-emerald-700",
  report_in_progress: "bg-amber-100 text-amber-700",
  qc_review: "bg-purple-100 text-purple-700",
  report_approved: "bg-emerald-200 text-emerald-800",
  needs_climb_inspection: "bg-orange-100 text-orange-700",
  insufficient_damage: "bg-red-100 text-red-700",
};

interface SlaIssueRow {
  id: string;
  homeowner_name: string | null;
  property_street: string | null;
  property_city: string | null;
  stage: string | null;
  owner_id: string | null;
  appointment_date: string | null;
  drone_completed_date: string | null;
}

// Stage list as a plain string[] — Supabase's .in() likes a regular array, and
// it spares us a triple-cast every call site below.
const DRONE_STAGE_LIST: string[] = [...DRONE_STAGES];

// Days_in_stage is meaningful only for currently-open work. Including the
// terminal stages (insufficient_damage / report_approved) would skew the
// average toward "tenure of closed deals" rather than "how long open deals
// have been sitting" — that's an intentional behavior change from the prior
// version, which silently averaged across the 1000-row truncated bucket.
// Bounding to open stages also keeps this query well under any row cap.
const OPEN_DRONE_STAGE_LIST: string[] = DRONE_STAGES.filter(
  (s) => s !== "insufficient_damage" && s !== "report_approved",
);

export default function ExecutiveDroneView() {
  const [stageCounts, setStageCounts] = useState<Record<string, number> | null>(null);
  const [kpis, setKpis] = useState<{ total: number; slaRate: number; approved: number; avgDays: number } | null>(null);
  const [slaIssues, setSlaIssues] = useState<SlaIssueRow[] | null>(null);
  const [members, setMembers] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        // Three days ago in ISO — cuts the SLA-issues fetch to only deals
        // that *could* be overdue. Without this we'd fetch all open drone
        // deals and filter client-side, which is what landed us at the
        // 1000-row cap in the first place.
        const threeDaysAgo = new Date(Date.now() - 3 * 86_400_000).toISOString();

        // Parallel: per-stage count (head:true, no rows fetched), SLA totals,
        // approved count, days-in-stage rows for open pipeline only,
        // narrowed SLA issues, team roster.
        const stageCountPromises = DRONE_STAGES.map(async (s) => {
          const { count, error } = await supabase
            .from("deals")
            .select("id", { count: "exact", head: true })
            .eq("stage", s);
          if (error) throw error;
          return [s, count ?? 0] as const;
        });

        const [
          stageCountEntries,
          slaTotalRes,
          slaMetRes,
          openDaysRes,
          slaIssuesRes,
          memsRes,
        ] = await Promise.all([
          Promise.all(stageCountPromises),
          supabase
            .from("deals")
            .select("id", { count: "exact", head: true })
            .in("stage", DRONE_STAGE_LIST)
            .not("drone_sla_met", "is", null),
          supabase
            .from("deals")
            .select("id", { count: "exact", head: true })
            .in("stage", DRONE_STAGE_LIST)
            .eq("drone_sla_met", true),
          supabase
            .from("deals")
            .select("days_in_stage,stage_entered_at")
            .in("stage", OPEN_DRONE_STAGE_LIST),
          supabase
            .from("deals")
            .select(
              "id,homeowner_name,property_street,property_city,stage,owner_id,appointment_date,drone_completed_date",
            )
            .in("stage", DRONE_STAGE_LIST)
            .is("drone_completed_date", null)
            .not("appointment_date", "is", null)
            .lt("appointment_date", threeDaysAgo)
            .order("appointment_date", { ascending: true }),
          supabase
            .from("team_roster")
            .select("id,full_name")
            .eq("is_active", true),
        ]);

        if (slaTotalRes.error) throw slaTotalRes.error;
        if (slaMetRes.error) throw slaMetRes.error;
        if (openDaysRes.error) throw openDaysRes.error;
        if (slaIssuesRes.error) throw slaIssuesRes.error;
        if (memsRes.error) throw memsRes.error;

        if (cancelled) return;

        // Stage counts → record
        const counts: Record<string, number> = {};
        for (const s of DRONE_STAGES) counts[s] = 0;
        let total = 0;
        for (const [stage, count] of stageCountEntries) {
          counts[stage] = count;
          total += count;
        }

        const slaTotal = slaTotalRes.count ?? 0;
        const slaMet = slaMetRes.count ?? 0;
        const slaRate = slaTotal > 0 ? Math.round((slaMet / slaTotal) * 100) : 100;
        const approved = counts["report_approved"] ?? 0;
        const openRows = (openDaysRes.data ?? []) as Array<{ days_in_stage: number | null; stage_entered_at: string | null }>;
        const totalDays = openRows.reduce((sum, r) => sum + daysInStage(r), 0);
        const avgDays = openRows.length > 0
          ? Math.round((totalDays / openRows.length) * 10) / 10
          : 0;

        setStageCounts(counts);
        setKpis({ total, slaRate, approved, avgDays });
        setSlaIssues((slaIssuesRes.data ?? []) as SlaIssueRow[]);
        const map: Record<string, string> = {};
        (memsRes.data ?? []).forEach((m) => { map[m.id] = m.full_name; });
        setMembers(map);
      } catch (e) {
        if (!cancelled) setErr(fmtSupabaseError(e));
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (err) return <div className="bg-red-50 text-red-700 p-4 rounded">{err}</div>;
  if (!stageCounts || !kpis || !slaIssues)
    return <div className="text-slate-500 py-8 text-center text-sm">Loading overview…</div>;

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPICard label="Total Pipeline" value={kpis.total} />
        <KPICard
          label="SLA Compliance"
          value={`${kpis.slaRate}%`}
          color={kpis.slaRate >= 90 ? "text-emerald-600" : kpis.slaRate >= 70 ? "text-amber-600" : "text-red-600"}
        />
        <KPICard label="Approved" value={kpis.approved} color="text-emerald-600" />
        <KPICard label="Avg Days in Stage" value={kpis.avgDays} />
      </div>

      {/* Stage breakdown */}
      <section>
        <h3 className="text-sm font-semibold text-bardin-ink mb-3">Pipeline by Stage</h3>
        <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
          {DRONE_STAGES.map((stage) => {
            const count = stageCounts[stage] ?? 0;
            return (
              <div
                key={stage}
                className={
                  "rounded-lg px-3 py-2 text-center " +
                  (STAGE_COLORS[stage] ?? "bg-slate-100 text-slate-600")
                }
              >
                <div className="text-lg font-bold">{count}</div>
                <div className="text-[10px] font-medium">{STAGE_LABELS[stage]}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* SLA Issues */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-sm font-semibold text-bardin-ink">SLA Issues</h3>
          {slaIssues.length > 0 && (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
              {slaIssues.length} overdue
            </span>
          )}
        </div>

        {slaIssues.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-4 text-sm text-emerald-600 text-center">
            All deals within 3-day SLA.
          </div>
        ) : (
          <div className="space-y-2">
            {slaIssues.slice(0, 10).map((d) => {
              const daysSince = d.appointment_date
                ? Math.floor((Date.now() - new Date(d.appointment_date).getTime()) / 86_400_000)
                : 0;
              const ownerName = d.owner_id ? members[d.owner_id] : null;

              return (
                <div key={d.id} className="bg-white rounded-xl shadow px-4 py-3 border-l-4 border-red-500">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-bardin-ink">
                          <DealLink id={d.id}>{d.homeowner_name ?? "(no name)"}</DealLink>
                        </span>
                        <span
                          className={
                            "text-[10px] font-medium px-2 py-0.5 rounded-full " +
                            (STAGE_COLORS[d.stage ?? ""] ?? "bg-slate-100 text-slate-600")
                          }
                        >
                          {STAGE_LABELS[d.stage ?? ""] ?? d.stage}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {isAddressLinkable(d) ? (
                          <a
                            href={mapsHref(d)}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="hover:underline hover:text-slate-700"
                          >
                            {[d.property_street, d.property_city].filter(Boolean).join(", ")}
                          </a>
                        ) : (
                          [d.property_street, d.property_city].filter(Boolean).join(", ") || "—"
                        )}
                      </div>
                      {ownerName && (
                        <div className="text-xs text-slate-400 mt-0.5">RM: {ownerName}</div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-xs text-red-600 font-semibold">{daysSince}d overdue</div>
                      <div className="text-[10px] text-slate-400">since appt</div>
                    </div>
                  </div>
                </div>
              );
            })}
            {slaIssues.length > 10 && (
              <div className="text-xs text-slate-400 text-center py-2">
                + {slaIssues.length - 10} more overdue deals — see All Flights tab
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function KPICard({
  label,
  value,
  color = "text-slate-900",
}: {
  label: string;
  value: string | number;
  color?: string;
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-center">
      <div className={`text-xl font-extrabold ${color}`}>{value}</div>
      <div className="text-[9px] font-semibold text-slate-500 uppercase tracking-wide mt-0.5">{label}</div>
    </div>
  );
}
